import { store } from '@/app/store';
import { sentryService } from '@/services/telemetry/sentry';
import { reloadLocation, replaceLocation } from '@/shared/lib/browser-location';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';

/**
 * Polls a build-version file to detect new deployments.
 * When a new version is detected and no user session is active,
 * forces a cache-busting reload so the kiosk picks up the latest code.
 *
 * On a 24/7 kiosk, the browser keeps the page loaded in memory forever.
 * Even Cloudflare cache purges don't help because the old JS is already
 * running. This service bridges that gap by proactively checking for
 * new deployments and triggering reloads at safe moments.
 */
class DeploymentVersion {
  /** How often to poll for new versions (ms) */
  POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

  /** Grace period after boot before polling starts (ms).
   *  Avoids reload loops if build-version.txt is briefly inconsistent during deploy. */
  BOOT_GRACE_MS = 60_000;

  /** Version string fetched at boot time */
  _currentVersion = null;

  /** Polling timer handle */
  _pollTimer = null;

  /** Whether initialization completed */
  _initialized = false;

  constructor() {
    exposeToGlobalScope(this, 'DeploymentVersion');
  }

  async initialize() {
    try {
      this._currentVersion = await this._fetchVersion();
      if (!this._currentVersion) {
        // build-version.txt doesn't exist (old deployment or dev mode) — skip polling
        console.info(
          '[DeploymentVersion] No build-version.txt found, version polling disabled'
        );
        return;
      }
      console.info(
        '[DeploymentVersion] Current build version:',
        this._currentVersion
      );
      this._initialized = true;

      // Start polling after grace period
      setTimeout(() => this._startPolling(), this.BOOT_GRACE_MS);
    } catch (e) {
      console.warn('[DeploymentVersion] Init failed:', e.message);
    }
  }

  /**
   * Fetch the build version from the server, bypassing all caches.
   * Returns null if the file doesn't exist or fetch fails.
   */
  async _fetchVersion() {
    try {
      const response = await fetch(`/build-version.txt?_=${Date.now()}`, {
        cache: 'no-store',
        // Extra headers to bypass CDN and proxy caches
        headers: {
          'Cache-Control': 'no-cache, no-store',
          Pragma: 'no-cache',
        },
      });
      if (!response.ok) return null;
      const text = (await response.text()).trim();
      // SPA-fallback servers (vite dev, misconfigured Caddy) answer 200
      // with index.html for a missing build-version.txt. Anything that
      // isn't a plain version token is "no version" -- the value ends up
      // in the X-Kiosk-Build-Version header, where a stray non-ISO-8859-1
      // character makes fetch() throw on EVERY api call and bricks the
      // kiosk at boot.
      if (!/^[\w.-]{1,64}$/.test(text)) return null;
      return text;
    } catch {
      return null;
    }
  }

  _startPolling() {
    if (this._pollTimer) return;

    this._pollTimer = setInterval(async () => {
      if (!(this._initialized && this._currentVersion)) return;

      try {
        const newVersion = await this._fetchVersion();
        if (!newVersion || newVersion === this._currentVersion) return;

        console.warn(
          `[DeploymentVersion] New version detected: ${newVersion} (running: ${this._currentVersion})`
        );

        sentryService.addBreadcrumb(
          'deployment',
          `New deployment detected: ${newVersion}`,
          {
            old_version: this._currentVersion,
            new_version: newVersion,
            has_session: !!store.sessionId,
          },
          'warning'
        );

        // Only reload if no active user session.
        // If a session is active, the next natural session end → hardRestart
        // will pick up the new code via cache-busting reload.
        if (!store.sessionId) {
          console.warn(
            '[DeploymentVersion] No active session — reloading for new deployment'
          );
          this._reloadWithCacheBust();
        } else {
          console.info(
            '[DeploymentVersion] Session active — will reload on next session end'
          );
          // Store the flag so hardRestart() knows a deployment happened.
          // Not strictly needed since hardRestart already uses cache-busting,
          // but useful for logging.
          this._pendingVersion = newVersion;
        }
      } catch {
        // Silent — network hiccups must not disrupt kiosk operation
      }
    }, this.POLL_INTERVAL_MS);
  }

  destroy() {
    if (this._pollTimer) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
  }

  /**
   * Force reload with a cache-busting query parameter.
   * This bypasses both browser cache and CDN edge caches
   * to ensure the fresh index.html (with new script hashes) is fetched.
   */
  _reloadWithCacheBust() {
    reloadWithCacheBust();
  }
}

/**
 * Reload the page with a cache-busting query parameter.
 * Exported as a standalone function so it can be used from UI.js,
 * app.js, and anywhere else that needs a guaranteed-fresh reload.
 *
 * Uses location.replace() to avoid polluting browser history
 * with cache-bust URLs on 24/7 kiosk.
 */
export function reloadWithCacheBust() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_v', Date.now().toString(36));
    replaceLocation(url.toString());
  } catch {
    // Fallback if URL parsing fails
    reloadLocation();
  }
}

export const deploymentVersion = new DeploymentVersion();
